import { Loader } from '@progress/kendo-react-indicators'
import { Tooltip } from '@progress/kendo-react-tooltip'
import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { HStack } from '~/components/elements/HStack'
import { Icon } from '~/components/elements/Icon'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { StyledButton } from '~/components/elements/StyledButton'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { useSurveyQueryAvailableBrandGroups } from '~/hooks/useSurveyQuery/useSurveyQueryAvailableBrandGroups'
import { useSurveyQueryData } from '~/hooks/useSurveyQuery/useSurveyQueryData'
import { useSurveyQueryQuestions } from '~/hooks/useSurveyQuery/useSurveyQueryQuestions'
import { buildSurveyFilterTextGroups } from '~/utils/specifics/buildSurveyFilterTextGroups'
import { formatFilterTextGroups } from '~/utils/specifics/formatFilterTextGroups'

import {
  activeCustomRoles,
  brandAlignedAtom,
  BUILTIN_QUESTION_ROLES,
  questionRoleDescriptions,
  questionRoleLabels,
} from '../atoms'
import { planRenderTasks, useBasicOutputWizard } from '../hooks'

type ConfirmationStepProps = {
  aiEnabled: boolean
  effectiveAiOn: boolean
  isAiStateResolved: boolean
  isSubmitting: boolean
  onBack: () => void
  onSubmit: () => void
}

export const ConfirmationStep = ({
  aiEnabled,
  effectiveAiOn,
  isAiStateResolved,
  isSubmitting,
  onBack,
  onSubmit,
}: ConfirmationStepProps) => {
  const survey = useSurveyQueryData()
  const brandGroups = useSurveyQueryAvailableBrandGroups()
  const questions = useSurveyQueryQuestions()
  const { t } = useTranslation()
  const {
    brandGroupId,
    customLabels,
    customRoleAxes,
    filterPatternDrafts,
    getCustomRoleAxis,
    isAllValid,
    isEntireBrandGroupSelected,
    keyCompetitorBrandIds,
    ownBrandIds,
    questionAssignments,
    selectedBrandIds,
  } = useBasicOutputWizard()
  const brandAligned = useAtomValue(brandAlignedAtom)

  const brandGroupName = React.useMemo(
    () => brandGroups.find(({ id }) => id === brandGroupId)?.name ?? '',
    [brandGroupId, brandGroups],
  )

  const resolveQuestionText = React.useCallback(
    (questionId: string) =>
      questions.find(({ question_id }) => question_id === questionId)
        ?.question_text ?? t('basic_output_confirm_unknown'),
    [questions, t],
  )

  const resolveCustomerAttributeGroupName = React.useCallback(
    (customerAttributeGroupId: string) =>
      (survey?.customerAttributeGroups ?? []).find(
        ({ id }) => id === customerAttributeGroupId,
      )?.name ?? t('basic_output_confirm_unknown'),
    [survey?.customerAttributeGroups, t],
  )

  const groupBrands = React.useMemo(
    () => brandGroups.find(({ id }) => id === brandGroupId)?.brands ?? [],
    [brandGroupId, brandGroups],
  )

  const selectedBrands = React.useMemo(
    () => groupBrands.filter((b) => selectedBrandIds.includes(b.id)),
    [groupBrands, selectedBrandIds],
  )

  const selectedBrandDisplayNames = React.useMemo(
    () => [
      ...(isEntireBrandGroupSelected
        ? [t('basic_output_whole_brand_group_label')]
        : []),
      ...selectedBrands.map((b) => b.name),
    ],
    [isEntireBrandGroupSelected, selectedBrands, t],
  )

  const { keyCompetitorDisplay, ownBrandDisplay } = React.useMemo(() => {
    // Roles are independent of 生成対象, so iterate the full brand group (not
    // selectedBrands) — this includes role-only brands and matches both the
    // Step-2 table order and what is sent to the AI prompt. A brand may appear
    // in BOTH rows when marked as both 自社 and 注目競合 (overlap is allowed).
    const ownNames = groupBrands
      .filter((b) => ownBrandIds.includes(b.id))
      .map((b) => b.name)
    const keyCompetitorNames = groupBrands
      .filter((b) => keyCompetitorBrandIds.includes(b.id))
      .map((b) => b.name)

    return {
      keyCompetitorDisplay:
        keyCompetitorNames.length > 0
          ? keyCompetitorNames.join('、')
          : t('basic_output_confirm_not_specified'),
      ownBrandDisplay:
        ownNames.length > 0
          ? ownNames.join('、')
          : t('basic_output_confirm_not_specified'),
    }
  }, [groupBrands, keyCompetitorBrandIds, ownBrandIds, t])

  // Single source of truth for the pre-generation count: the engine-free render
  // plan. planRenderTasks derives the table count from wizard input alone, so the
  // ConfirmationStep estimate and the actual render loop can never disagree.
  const outputCount = React.useMemo(
    () =>
      planRenderTasks({
        aiCommentsEnabled: effectiveAiOn,
        brandIdToOriginalIndex: new Map(groupBrands.map((b, i) => [b.id, i])),
        customerAttributeGroups: survey?.customerAttributeGroups ?? [],
        customRoleAxes,
        filterPatternDrafts,
        getBrandAligned: (role) => brandAligned[role] ?? true,
        groupBrands,
        isEntireBrandGroupSelected,
        ownBrandIds,
        questionAssignments,
        questions,
        selectedBrandIds,
      }).length,
    [
      brandAligned,
      customRoleAxes,
      effectiveAiOn,
      filterPatternDrafts,
      groupBrands,
      isEntireBrandGroupSelected,
      ownBrandIds,
      questionAssignments,
      questions,
      selectedBrandIds,
      survey?.customerAttributeGroups,
    ],
  )

  return (
    <VStack spacing={4}>
      <LabelingContainer label={t('basic_output_survey_label')}>
        <Typography variant="normal">{survey?.title ?? ''}</Typography>
      </LabelingContainer>

      <LabelingContainer label={t('basic_output_brand_group_label')}>
        <Typography variant="normal">{brandGroupName}</Typography>
      </LabelingContainer>

      <LabelingContainer label={t('basic_output_confirm_selected_brand_label')}>
        <Typography variant="normal">
          {selectedBrandDisplayNames.length === 0
            ? t('basic_output_confirm_not_selected')
            : selectedBrandDisplayNames.join('、')}
        </Typography>
      </LabelingContainer>

      <LabelingContainer label={t('basic_output_brand_own_label')}>
        <Typography variant="normal">{ownBrandDisplay}</Typography>
      </LabelingContainer>

      <LabelingContainer label={t('basic_output_brand_key_competitor_label')}>
        <Typography variant="normal">{keyCompetitorDisplay}</Typography>
      </LabelingContainer>

      {BUILTIN_QUESTION_ROLES.map((role) => {
        const questionIds = questionAssignments[role] ?? []

        return (
          <div key={role}>
            <div style={{ alignItems: 'center', display: 'flex', gap: '4px' }}>
              <Typography variant="normal">
                {t(questionRoleLabels[role])}
              </Typography>
              <Tooltip anchorElement="target" openDelay={0} position="right">
                <Icon
                  icon="help"
                  size="1rem"
                  title={t(questionRoleDescriptions[role])}
                />
              </Tooltip>
            </div>
            <div style={{ marginTop: '4px' }}>
              <Typography variant="normal">
                {questionIds.length === 0
                  ? t('basic_output_confirm_not_selected')
                  : questionIds
                      .map((qId) => resolveQuestionText(qId))
                      .join(', ')}
              </Typography>
            </div>
          </div>
        )
      })}

      {activeCustomRoles(questionAssignments).map((role) => {
        const selectedIds = questionAssignments[role] ?? []
        const isCustomerAttributeAxis =
          getCustomRoleAxis(role) === 'customerAttribute'
        // 顧客属性軸の枠が持つIDは設問IDではないので、設問として引くと必ず「不明」になる
        const resolveSelectedText = isCustomerAttributeAxis
          ? resolveCustomerAttributeGroupName
          : resolveQuestionText

        return (
          <LabelingContainer
            key={role}
            label={t(
              isCustomerAttributeAxis
                ? 'basic_output_confirm_custom_customer_attribute_label'
                : 'basic_output_confirm_custom_question_label',
              { label: customLabels[role] ?? '' },
            )}
          >
            <Typography variant="normal">
              {selectedIds.length === 0
                ? t('basic_output_confirm_not_selected')
                : selectedIds.map((id) => resolveSelectedText(id)).join(', ')}
            </Typography>
          </LabelingContainer>
        )
      })}

      <LabelingContainer label={t('basic_output_confirm_filter_label')}>
        <VStack spacing={1}>
          {filterPatternDrafts.map((draft, index) => {
            const filterDetail =
              draft.filterGroups.length === 0
                ? t('basic_output_confirm_filter_all_data')
                : formatFilterTextGroups(
                    buildSurveyFilterTextGroups({
                      brandGroups: survey?.brandGroups ?? [],
                      customerAttributeGroups:
                        survey?.customerAttributeGroups ?? [],
                      filterGroups: draft.filterGroups,
                      questions: survey?.questions ?? [],
                      t,
                    }),
                  )

            return (
              <Typography key={index} variant="normal">
                {t('basic_output_confirm_filter_entry', {
                  detail: filterDetail,
                  name:
                    draft.name ||
                    t('basic_output_filter_default_name', {
                      index: index + 1,
                    }),
                })}
              </Typography>
            )
          })}
        </VStack>
      </LabelingContainer>

      <LabelingContainer label={t('basic_output_confirm_output_count_label')}>
        <Typography variant="normal">
          {t('basic_output_confirm_output_count_value', { count: outputCount })}
        </Typography>
      </LabelingContainer>

      <LabelingContainer label={t('confirmation_ai_toggle_label')}>
        {isAiStateResolved ? (
          <VStack spacing={1}>
            <Typography variant="normal">
              {effectiveAiOn ? t('on') : t('off')}
            </Typography>
            {!effectiveAiOn && (
              <Typography
                color={!aiEnabled ? 'error' : 'light'}
                variant="small"
              >
                {!aiEnabled
                  ? t('ai_disabled_message')
                  : t('confirmation_ai_off_hint')}
              </Typography>
            )}
          </VStack>
        ) : (
          // Query still loading (or errored — the wizard's error banner above
          // already explains that case). aiEnabled/effectiveAiOn default to
          // false while unresolved, so rendering this as "OFF" here would show
          // a false "AI off" state instead of the actual loading state.
          <Loader size="small" type="infinite-spinner" />
        )}
      </LabelingContainer>

      <HStack align="center" spacing={2}>
        <StyledButton
          buttonStyle="normal"
          disabled={isSubmitting}
          onClick={onBack}
        >
          {t('basic_output_back')}
        </StyledButton>
        <StyledButton
          buttonStyle="success"
          disabled={isSubmitting || !isAllValid}
          onClick={onSubmit}
        >
          {t('basic_output_confirm_generate')}
        </StyledButton>
      </HStack>
    </VStack>
  )
}
